import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Cascader,
  Checkbox,
  Dropdown,
  Modal,
  Segmented,
  Select,
  Slider,
  SliderSingleProps,
  Typography,
} from 'antd';
import type { InputNumberProps } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { Form, Icon, Input } from 'ui';

import { termsSelector } from 'selectors/propertySlice.selector';
import { setTerms } from 'slices/propertySlice';
import { useAppDispatch, useAppSelector } from 'store/index';

import { CommercialTypeEnum, PostingTypeEnum } from 'types/property/propertyEnum';
import { FilterType } from 'types/property/propertyTypes';

import s from './NavBar.module.scss';

type CascaderValue = (string | number)[] | (string | number)[][];

function isMulti(value: CascaderValue): value is (string | number)[][] {
  return Array.isArray(value) && Array.isArray(value[0]);
}

const Filters: FC = () => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(termsSelector);
  const [price, setPrice] = useState<number | number[]>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [priceDropdownOpen, setPriceDropdownOpen] = useState(false);
  const [rentOption, setRentOption] = useState<PostingTypeEnum>();
  const [bedRoomCount, setBedRoomCount] = useState<number>();

  const noFilter = useWatch('noFilter', form);

  type ValueType = NonNullable<InputNumberProps['value']>;

  useEffect(() => {
    if (noFilter) {
      // Clear values when noFilter is true
      form.setFieldsValue({
        fromSize: undefined,
        toSize: undefined,
      });
    }
  }, [noFilter, form]);

  const updateTerms = useCallback(
    (terms: Partial<FilterType>) => {
      dispatch(setTerms(terms));
    },
    [dispatch],
  );

  // const onChangeComplete = (value: number | number[]) => {
  //   if (Array.isArray(value)) {
  //     updateTerms({ priceFrom: value[0], priceTo: value[1] });
  //   }
  // };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = useCallback(
    async (data) => {
      setIsModalOpen(false);
      updateTerms({ otherFilters: data });
    },
    [updateTerms],
  );

  const handleChange = (value: CascaderValue) => {
    // Normalize to array of paths (multiple mode)
    const paths = isMulti(value) ? value : value ? [value] : [];

    // Filter out empty or invalid paths
    const validPaths = paths.filter((p) => Array.isArray(p) && p.length > 0);

    if (!validPaths.length) {
      // Cascader cleared — reset terms
      updateTerms({ commercialType: undefined, propertyTypes: [] });
      return;
    }

    // Level 1 → commercialType
    const commercialType = String(validPaths[0][0]);

    // Only take last child of each path
    const propertyTypes = Array.from(new Set(validPaths.map((p) => String(p[p.length - 1]))));

    updateTerms({
      commercialType: commercialType as CommercialTypeEnum,
      propertyTypes,
    });
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  const formatCurrency = (value) => {
    return `€${value?.toLocaleString()}`;
  };

  const getFormattedRange = (min, max) => {
    const minCurrency = min;
    const maxCurrency = max;

    return `${formatCurrency(minCurrency)} — ${formatCurrency(maxCurrency)}`;
  };

  const rentFilter = [
    { label: 'All', value: 'all' },
    { label: 'FreeHold', value: 'freeHold' },
    { label: 'GroundRent', value: 'groundRent' },
  ];
  const typeRentFilter = [
    { label: 'All', value: 'all' },
    { label: 'Perpetual', value: 'perpetual' },
    { label: 'Temporary', value: 'temporary' },
  ];
  const orientationFilter = [
    { label: 'All', value: 'all' },
    { label: 'North', value: 'north' },
    { label: 'East', value: 'east' },
    { label: 'West', value: 'west' },
    { label: 'South', value: 'south' },
  ];

  const rentOptions = [
    {
      value: PostingTypeEnum.Sale,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
          <Icon name="pin" color="#8E53E8" />
          Sale
        </div>
      ),
    },
    {
      value: PostingTypeEnum.Rent,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
          <Icon name="share" />
          Rent
        </div>
      ),
    },
    {
      value: PostingTypeEnum.Share,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
          <Icon name="pin" color="#00A743" />
          Share
        </div>
      ),
    },
    {
      value: PostingTypeEnum.All,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 600 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Icon name="pin" color="#8E53E8" />
            <Icon name="share" />
            <Icon name="pin" color="#00A743" />
          </div>
          Sale, Rent & Share
        </div>
      ),
    },
  ];

  // const apartmentOptions = [
  //   {
  //     value: 'apartment',
  //     label: 'Apartment',
  //   },
  //   {
  //     value: 'house',
  //     label: 'House',
  //   },

  //   {
  //     value: 'commercial',
  //     label: 'Commercial',
  //   },

  //   {
  //     value: 'plot',
  //     label: 'Plot/Field',
  //   },
  // ];

  const bedRoomOption = [
    {
      value: -1,
      label: 'Any # Bedrooms',
    },
    {
      value: 1,
      label: '1 Bedroom',
    },
    {
      value: 2,
      label: '2 Bedrooms',
    },
    {
      value: 3,
      label: '3 Bedrooms',
    },
    {
      value: 4,
      label: '4 Bedrooms',
    },
    {
      value: 5,
      label: '5 Bedrooms',
    },
  ];

  const propertyOptions = useMemo(
    () => [
      {
        value: 'Residential',
        label: 'Residential',
        children: [
          {
            value: 'Apartment',
            label: 'Apartment',
            children: [
              { value: 'Apartment', label: 'Apartment' },
              { value: 'Maisonette', label: 'Maisonette' },
              { value: 'Ground Floor Maisonette', label: 'Ground Floor Maisonette' },
              { value: 'Penthouse', label: 'Penthouse' },
              { value: 'Studio', label: 'Studio' },
            ],
          },
          {
            value: 'House',
            label: 'House',
            children: [
              { value: 'Terraced house', label: 'Terraced house' },
              { value: 'Townhouse', label: 'Townhouse' },
              { value: 'Semi detached villa', label: 'Semi detached villa' },
              { value: 'Detached villa', label: 'Detached villa' },
              { value: 'Farmhouse', label: 'Farmhouse' },
              { value: 'House of Character', label: 'House of Character' },
              { value: 'Bungalow', label: 'Bungalow' },
              { value: 'Palazzo, Manor', label: 'Palazzo, Manor' },
            ],
          },
          {
            value: 'Garage',
            label: 'Garage',
            children: [
              { value: 'Lockup Garage', label: 'Lockup Garage' },
              { value: 'Car Space', label: 'Car Space' },
            ],
          },
          {
            value: 'Plot, land or field',
            label: 'Plot, land or field',
            children: [
              { value: 'Plot', label: 'Plot' },
              { value: 'Site for development', label: 'Site for development' },
              { value: 'Land', label: 'Land' },
              { value: 'Plot for development', label: 'Plot for development' },
              { value: 'Field', label: 'Field' },
              { value: 'ODZ Land', label: 'ODZ Land' },
            ],
          },
        ],
      },
      {
        value: 'Commercial',
        label: 'Commercial',
        children: [
          {
            value: 'Office',
            label: 'Office',
            children: [{ value: 'Office', label: 'Office' }],
          },
          {
            value: 'Retail',
            label: 'Retail',
            children: [
              { value: 'Shop', label: 'Shop' },
              { value: 'Showroom', label: 'Showroom' },
              { value: 'Clinic', label: 'Clinic' },
            ],
          },
          {
            value: 'Catering',
            label: 'Catering',
            children: [
              { value: 'Restaurant', label: 'Restaurant' },
              { value: 'Take-Away', label: 'Take-Away' },
              { value: 'Bar', label: 'Bar' },
              { value: 'Club', label: 'Club' },
            ],
          },
          {
            value: 'Hospitality',
            label: 'Hospitality',
            children: [
              { value: 'Hotel', label: 'Hotel' },
              { value: 'GuestHouse', label: 'GuestHouse' },
            ],
          },
          {
            value: 'WareHouse',
            label: 'WareHouse',
            children: [{ value: 'WareHouse', label: 'WareHouse' }],
          },
          {
            value: 'Industrial',
            label: 'Industrial',
            children: [
              { value: 'Factory', label: 'Factory' },
              { value: 'Workshop', label: 'Workshop' },
            ],
          },
        ],
      },
    ],
    [],
  );

  const propertyTypeValue = useMemo(() => {
    const savedPropertyTypes = terms?.propertyTypes;
    const selectedTypes = Array.isArray(savedPropertyTypes)
      ? savedPropertyTypes
      : savedPropertyTypes
      ? [savedPropertyTypes]
      : [];

    if (!selectedTypes.length) {
      return [];
    }

    const findPath = (options, target: string, currentPath: string[] = []) => {
      for (const option of options) {
        const nextPath = [...currentPath, option.value];

        if (option.value === target && !option.children?.length) {
          return nextPath;
        }

        if (option.children) {
          const childPath = findPath(option.children, target, nextPath);

          if (childPath) {
            return childPath;
          }
        }
      }

      return undefined;
    };

    return selectedTypes
      .map((type) => findPath(propertyOptions, String(type)))
      .filter((path): path is string[] => Boolean(path));
  }, [propertyOptions, terms?.propertyTypes]);

  const modalFilterCount = useMemo(() => {
    const otherFilters = terms?.otherFilters;

    if (!otherFilters) {
      return 0;
    }

    return [
      otherFilters.groundRentType && otherFilters.groundRentType !== 'all',
      otherFilters.groundRentStatus && otherFilters.groundRentStatus !== 'all',
      otherFilters.orientation && otherFilters.orientation !== 'all',
      Array.isArray(otherFilters.finishStatus) && otherFilters.finishStatus.length > 0,
      otherFilters.fromSize !== undefined || otherFilters.toSize !== undefined,
    ].filter(Boolean).length;
  }, [terms?.otherFilters]);

  const hasActiveFilters = useMemo(() => {
    const savedPropertyTypes = terms?.propertyTypes;
    const selectedPropertyTypes = Array.isArray(savedPropertyTypes)
      ? savedPropertyTypes
      : savedPropertyTypes
      ? [savedPropertyTypes]
      : [];

    return Boolean(
      (terms?.postingType && terms.postingType !== PostingTypeEnum.All) ||
        selectedPropertyTypes.length > 0 ||
        (terms?.numberOfBedrooms !== undefined && terms.numberOfBedrooms !== -1) ||
        terms?.priceFrom !== undefined ||
        terms?.priceTo !== undefined ||
        modalFilterCount > 0,
    );
  }, [modalFilterCount, terms]);

  const modalFilterLabel =
    modalFilterCount > 0 ? `More Filters (${modalFilterCount})` : 'More Filters';

  const isMobile = window.innerWidth < 768;

  const MAX_PRICE = 5_000_000;
  const MIN_PRICE_GAP = 20_000;

  const resetFilterForm = useCallback(() => {
    form.setFieldsValue({
      groundRentType: 'all',
      groundRentStatus: 'all',
      orientation: 'all',
      fromSize: undefined,
      toSize: undefined,
      finishStatus: undefined,
      noFilter: true,
    });
  }, [form]);

  const clampRange = (range: number[]): number[] => {
    const [rawFrom, rawTo] = range;
    let from = Math.max(0, rawFrom);
    let to = Math.min(MAX_PRICE, rawTo);

    if (to - from < MIN_PRICE_GAP) {
      if (from <= 0) {
        to = MIN_PRICE_GAP;
      } else if (to >= MAX_PRICE) {
        from = MAX_PRICE - MIN_PRICE_GAP;
      } else {
        to = from + MIN_PRICE_GAP;
      }
    }

    return [from, to];
  };

  const getCurrentPriceRange = (): [number, number] => {
    if (Array.isArray(price) && price.length === 2) {
      return [price[0], price[1]];
    }
    return [
      terms?.priceFrom ? Number(terms.priceFrom) : 0,
      terms?.priceTo ? Number(terms.priceTo) : MAX_PRICE,
    ];
  };

  const handlePriceInputChange = (index: 0 | 1) => (value: ValueType | null) => {
    const n = value == null ? 0 : typeof value === 'number' ? value : Number(value);

    const [from, to] = getCurrentPriceRange();
    const next: [number, number] = index === 0 ? [n, to] : [from, n];
    const clamped = clampRange(next);

    setPrice(clamped);
    updateTerms({ priceFrom: clamped[0], priceTo: clamped[1] });
  };

  const marks: SliderSingleProps['marks'] = {
    0: '€0',

    5000000: {
      style: {
        left: isMobile ? 170 : 330,
      },
      label: <>€5,000,000</>,
    },
  };

  const priceOverlay = (
    <div
      className={s.priceOverlayDisabled}
      role="button"
      tabIndex={0}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // satisfy keyboard interaction expectation
        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
      }}>
      <Slider
        marks={marks}
        range
        min={0}
        max={MAX_PRICE}
        step={1000}
        value={getCurrentPriceRange()}
        onChange={(val) => {
          if (!Array.isArray(val)) return;
          setPrice(clampRange(val));
        }}
        tooltip={{
          formatter: (value) => `€ ${value?.toLocaleString()}`,
        }}
        defaultValue={[0, MAX_PRICE]}
        onChangeComplete={(val) => {
          if (!Array.isArray(val)) return;
          const next = clampRange(val as number[]);
          setPrice(next);
          updateTerms({ priceFrom: next[0], priceTo: next[1] });
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
        <Input.Number
          min={0}
          max={MAX_PRICE - MIN_PRICE_GAP}
          value={getCurrentPriceRange()[0]}
          onChange={handlePriceInputChange(0)}
          placeholder="Min price"
          formatter={(v) =>
            v === undefined || v === null ? '' : `€ ${Number(v).toLocaleString()}`
          }
          parser={(v) => (v ? Number(String(v).replace(/[^0-9]/g, '')) : 0)}
          style={{ width: '100%' }}
        />
        <Input.Number
          min={MIN_PRICE_GAP}
          max={MAX_PRICE}
          value={getCurrentPriceRange()[1]}
          onChange={handlePriceInputChange(1)}
          placeholder="Max price"
          formatter={(v) =>
            v === undefined || v === null ? '' : `€ ${Number(v).toLocaleString()}`
          }
          parser={(v) => (v ? Number(String(v).replace(/[^0-9]/g, '')) : 0)}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  const handleRentOption = (value: PostingTypeEnum) => {
    setRentOption(value);
    updateTerms({ postingType: value });
  };

  // const handleApartmentOption = (value: string | string[]) => {
  //   setApartmentOption(value);
  //   updateTerms({ apartOption: value });
  // };
  const handleBedroomOption = (value: number) => {
    setBedRoomCount(value);
    updateTerms({ numberOfBedrooms: value });
  };

  const handleClearAll = useCallback(() => {
    setRentOption(PostingTypeEnum.All);
    setBedRoomCount(-1);
    setPrice([0, MAX_PRICE]);
    resetFilterForm();
    updateTerms({
      postingType: PostingTypeEnum.All,
      propertyTypes: [],
      numberOfBedrooms: -1,
      commercialType: undefined,
      priceFrom: undefined,
      priceTo: undefined,
      otherFilters: undefined,
    });
  }, [resetFilterForm, updateTerms]);

  // const handleSearchName = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   setSearchName(e.target.value);
  // };
  useEffect(() => {
    if (terms) {
      setRentOption(terms.postingType);
      // setApartmentOption(terms.apartOption);
      setBedRoomCount(terms.numberOfBedrooms);
      if (terms.priceFrom !== undefined && terms.priceTo !== undefined) {
        setPrice(clampRange([Number(terms.priceFrom), Number(terms.priceTo)]));
      } else {
        setPrice([0, MAX_PRICE]);
      }
      if (form) {
        form.setFieldsValue({
          groundRentType: terms.otherFilters?.groundRentType ?? 'all',
          groundRentStatus: terms.otherFilters?.groundRentStatus ?? 'all',
          orientation: terms.otherFilters?.orientation ?? 'all',
          fromSize: terms.otherFilters?.fromSize,
          toSize: terms.otherFilters?.toSize,
          finishStatus: terms.otherFilters?.finishStatus,
          noFilter: !terms.otherFilters?.fromSize && !terms.otherFilters?.toSize && true,
        });
      }
    }
  }, [terms, form, resetFilterForm]);

  return (
    <div>
      {location.pathname === '/' && (
        <div className={s.searchForm}>
          {/* <Select
            className={`${s.typeSelect} ${s.customSelectPlaceholder} ${
              apartmentOption?.length === 5
                ? s.width520
                : apartmentOption?.length === 4
                ? s.width420
                : apartmentOption?.length === 3
                ? s.width320
                : apartmentOption?.length === 2
                ? s.width220
                : s.width120
            }`}
            mode="multiple"
            placeholder="Select Property Type"
            value={apartmentOption}
            onChange={handleApartmentOption}
            options={apartmentOptions}></Select> */}

          {isMobile ? (
            <>
              <Button
                htmlType="button"
                onClick={showModal}
                className={s.mobileFilterButton}
                icon={<Icon name="filter" size={16} />}
                aria-label="Open filters">
                {modalFilterCount > 0 && (
                  <span className={s.mobileFilterCount}>({modalFilterCount})</span>
                )}
              </Button>
              {hasActiveFilters && (
                <Button
                  htmlType="button"
                  onClick={handleClearAll}
                  className={s.mobileFilterButton}
                  icon={<Icon name="close" size={16} />}
                  aria-label="Clear filters"
                />
              )}
            </>
          ) : (
            <>
              <Select
                className={s.rentSelect}
                value={rentOption}
                onChange={handleRentOption}
                options={rentOptions}></Select>
              <Cascader
                value={propertyTypeValue}
                onChange={handleChange}
                options={propertyOptions}
                placeholder="Select property type"
                multiple
                maxTagCount="responsive"
                showCheckedStrategy={Cascader.SHOW_CHILD}
                className={s.cascader}
                dropdownStyle={{
                  maxWidth: '90vw', // never overflow viewport
                  overflowX: 'scroll', // allow horizontal scroll if needed
                }}
              />
              <Select
                className={s.bedSelect}
                defaultValue={-1}
                value={bedRoomCount}
                onChange={handleBedroomOption}
                options={bedRoomOption}></Select>
              <Dropdown
                menu={{ items: [] }}
                dropdownRender={() => priceOverlay}
                placement="bottom"
                trigger={['click']}
                open={priceDropdownOpen}
                onOpenChange={setPriceDropdownOpen}>
                <Button className={s.dropdownButton}>
                  {!price ? 'Price' : getFormattedRange(price[0] ?? 0, price[1] ?? 5000000)}
                </Button>
              </Dropdown>
              <Button htmlType="button" onClick={showModal} className={s.dropdownButton}>
                {modalFilterLabel}
              </Button>
              {hasActiveFilters && (
                <Button htmlType="button" onClick={handleClearAll} className={s.dropdownButton}>
                  Clear Filters
                </Button>
              )}
            </>
          )}
          {/* <Input
            autoComplete="off"
            className={s.searchInput}
            value={searchName}
            onChange={handleSearchName}
            fullWidth
            name="search"
            icon="search"
            placeholder="Street or Town"
          /> */}
        </div>
      )}
      <Modal
        centered
        open={isModalOpen}
        onOk={handleOk}
        footer={null}
        width={1000}
        onCancel={handleCancel}>
        <Form className={s.form} form={form} onFinish={handleSubmit} layout="vertical">
          {isMobile && (
            <div className={s.mobilePrimaryFilters}>
              <Typography
                style={{ fontSize: 24, marginBottom: 16, width: '100%', fontWeight: 700 }}>
                More filters
              </Typography>
              <div className={s.modalItem}>
                <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                  Type of posting
                </Typography>
                <div style={{ width: '100%' }}>
                  <Select
                    className={s.rentSelect}
                    value={rentOption}
                    onChange={handleRentOption}
                    options={rentOptions}></Select>
                </div>
              </div>
              <div className={s.modalItem}>
                <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                  Property Type
                </Typography>
                <div style={{ width: '100%' }}>
                  <Cascader
                    value={propertyTypeValue}
                    onChange={handleChange}
                    options={propertyOptions}
                    placeholder="Select property type"
                    multiple
                    maxTagCount="responsive"
                    showCheckedStrategy={Cascader.SHOW_CHILD}
                    className={s.cascader}
                    dropdownStyle={{
                      maxWidth: '90vw', // never overflow viewport
                      overflowX: 'scroll', // allow horizontal scroll if needed
                    }}
                  />
                </div>
              </div>
              <div className={s.modalItem}>
                <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                  Bedrooms
                </Typography>
                <div style={{ width: '100%' }}>
                  <Select
                    className={s.bedSelect}
                    defaultValue={-1}
                    value={bedRoomCount}
                    onChange={handleBedroomOption}
                    options={bedRoomOption}></Select>
                </div>
              </div>
              <div className={s.modalItem}>
                <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                  Price
                </Typography>
                <div className={s.mobilePricePanel}>{priceOverlay}</div>
              </div>
            </div>
          )}
          <div className={s.filter}>
            {!isMobile && (
              <Typography
                style={{ fontSize: 32, marginBottom: 16, width: '100%', fontWeight: 700 }}>
                More filters
              </Typography>
            )}
            {/* <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Type of posting
              </Typography>
              <div style={{ width: '100%' }}>
                <Form.Item name="postType" initialValue="rent">
                  <Radio.Group
                    className={s.filterSwitch}
                    options={postFilter}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
              </div>
            </div> */}
            <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Ground Rent
              </Typography>
              <div style={{ width: '100%' }}>
                <Form.Item name="groundRentType" initialValue="freeHold">
                  <Segmented
                    className={s.filterSwitch}
                    options={rentFilter}
                    block // makes it stretch full width (optional)
                  />
                </Form.Item>
              </div>
            </div>
            <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Type of Ground Rent
              </Typography>
              <div style={{ width: '100%' }}>
                <Form.Item name="groundRentStatus" initialValue="perpetual">
                  <Segmented
                    className={s.filterSwitch}
                    options={typeRentFilter}
                    block // makes it stretch full width (optional)
                  />
                </Form.Item>
              </div>
            </div>
            <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Orientation
              </Typography>
              <div style={{ width: '100%' }}>
                <Form.Item name="orientation" initialValue="north">
                  <Segmented
                    className={s.filterSwitch}
                    options={orientationFilter}
                    block // makes it stretch full width (optional)
                  />
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={s.checkboxes} style={{ paddingTop: 24 }}>
            <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Finish Type
              </Typography>

              <Form.Item name="finishStatus" style={{ marginLeft: 16 }}>
                <Checkbox.Group
                  style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                  <Checkbox style={{ marginBottom: 4 }} value="finished">
                    Finished
                  </Checkbox>
                  <Checkbox style={{ marginBottom: 4 }} value="partlyFinished">
                    Partly Finished
                  </Checkbox>
                  <Checkbox style={{ marginBottom: 4 }} value="shell">
                    Shell
                  </Checkbox>
                  <Checkbox style={{ marginBottom: 4 }} value="furnished">
                    Furnished
                  </Checkbox>
                  <Checkbox style={{ marginBottom: 4 }} value="partlyFurnished">
                    Partly Furnished
                  </Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </div>
            <div className={s.modalItem}>
              <Typography style={{ fontSize: 16, width: '100%', fontWeight: 600 }}>
                Size m²
              </Typography>
              <div className={s.SizeFilter}>
                <Form.Item name="noFilter" valuePropName="checked">
                  <Checkbox style={{ marginBottom: 4 }} value="noFilter">
                    No Size filter
                  </Checkbox>
                </Form.Item>
                <Form.Item name="fromSize" className={s.sizeInput}>
                  <Input.Number
                    style={{ width: '100%' }}
                    min={0}
                    name="fromSize"
                    disabled={noFilter}
                    placeholder="From"></Input.Number>
                </Form.Item>
                <Form.Item name="toSize" className={s.sizeInput}>
                  <Input.Number
                    style={{ width: '100%' }}
                    min={0}
                    name="toSize"
                    disabled={noFilter}
                    placeholder="To"></Input.Number>
                </Form.Item>
              </div>
            </div>
          </div>
          <div>
            <div className={s.actionButton}>
              {/* <pre>{JSON.stringify(payload, null, 2)}</pre> */}
              <Button className={s.cancel} onClick={handleCancel}>
                CANCEL
              </Button>
              <Button type="primary" className={s.result} htmlType="submit">
                SHOW RESULTS
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
export default Filters;
